/**
 * Tim Kirk Creative — Gallery Grid
 * Client-side filtering and lightbox for the gallery-grid section.
 * Self-contained vanilla JS (no ES module imports), loaded with defer.
 */
(function () {
  "use strict";

  var CATEGORY_LABELS = {
    illustration: "Illustration",
    // Slug stays childrens-books (product tags); label follows Tim's Aug 2026 naming.
    "childrens-books": "Illustration for Children",
    "theme-parks": "Theme Parks",
    "character-design": "Character Design",
  };

  var CATEGORY_PAGES = {
    illustration: "/pages/illustration",
    "childrens-books": "/pages/childrens-books",
    "theme-parks": "/pages/theme-parks",
    "character-design": "/pages/character-design",
  };

  // ─── Init ──────────────────────────────────────────────────────────────────

  function init() {
    var sections = document.querySelectorAll("[data-gallery-section]");
    for (var s = 0; s < sections.length; s++) {
      initSection(sections[s]);
    }
  }

  function initSection(sectionEl) {
    var masonry = sectionEl.querySelector("[data-gallery-masonry]");
    var filterGroup = sectionEl.querySelector("[data-gallery-filters]");
    // Dialog lives outside the section wrapper (to avoid stacking context issues)
    // but is still scoped by being inside the same shopify-section wrapper
    var shopifySection =
      sectionEl.closest(".shopify-section") || sectionEl.parentElement;
    var dialog = shopifySection
      ? shopifySection.querySelector("[data-gallery-lightbox]")
      : document.querySelector("[data-gallery-lightbox]");

    if (!masonry) return; // empty state — no products rendered

    var allItems = buildItemIndex(masonry);
    var visibleItems = allItems.slice();

    function getVisible() {
      return visibleItems;
    }

    if (filterGroup) {
      initFilters(filterGroup, allItems, function (activeCat) {
        for (var i = 0; i < allItems.length; i++) {
          var show = activeCat === "all" || allItems[i].category === activeCat;
          allItems[i].el.classList.toggle("gallery-grid__item--hidden", !show);
        }
        visibleItems =
          activeCat === "all"
            ? allItems.slice()
            : allItems.filter(function (item) {
                return item.category === activeCat;
              });
      });
    }

    if (dialog) {
      initLightbox(dialog, masonry, getVisible);
    }

    protectArtwork(sectionEl);
    if (dialog) protectArtwork(dialog);
  }

  // ─── Artwork Protection ─────────────────────────────────────────────────────
  // Soft deterrent against casual right-click/drag saving. The real protection
  // is the baked-in watermark on every uploaded image (scripts/upload-gallery.mjs).

  function protectArtwork(rootEl) {
    rootEl.addEventListener("contextmenu", function (e) {
      if (
        e.target.tagName === "IMG" ||
        e.target.closest(".gallery-grid__item, .gallery-lightbox__media")
      ) {
        e.preventDefault();
      }
    });
    rootEl.addEventListener("dragstart", function (e) {
      if (e.target.tagName === "IMG") e.preventDefault();
    });
  }

  // ─── Item Index ────────────────────────────────────────────────────────────

  function buildItemIndex(masonry) {
    var triggers = masonry.querySelectorAll("[data-lightbox-trigger]");
    var items = [];
    for (var i = 0; i < triggers.length; i++) {
      var btn = triggers[i];
      items.push({
        globalIndex: parseInt(btn.dataset.index, 10),
        title: btn.dataset.title || "",
        description: btn.dataset.description || "",
        category: btn.dataset.category || "",
        fullUrl: btn.dataset.fullUrl || "",
        thumbUrl: btn.dataset.thumbUrl || "",
        ratio: parseFloat(btn.dataset.ratio) || 0,
        altText: btn.dataset.alt || "",
        el: btn.closest(".gallery-grid__item"),
      });
    }
    return items;
  }

  // ─── Filtering ─────────────────────────────────────────────────────────────

  function initFilters(filterGroup, allItems, onFilter) {
    var active = "all";

    filterGroup.addEventListener("click", function (e) {
      var pill = e.target.closest("[data-filter]");
      if (!pill) return;

      var next = pill.dataset.filter;
      if (next === active) return;
      active = next;

      var pills = filterGroup.querySelectorAll("[data-filter]");
      for (var i = 0; i < pills.length; i++) {
        var isActive = pills[i].dataset.filter === active;
        pills[i].classList.toggle(
          "gallery-grid__filter-pill--active",
          isActive,
        );
        pills[i].setAttribute("aria-pressed", isActive ? "true" : "false");
      }

      onFilter(active);
    });
  }

  // ─── Lightbox ──────────────────────────────────────────────────────────────

  function initLightbox(dialog, masonry, getVisible) {
    var imgEl = dialog.querySelector("[data-lb-image]");
    var titleEl = dialog.querySelector("[data-lb-title]");
    var descEl = dialog.querySelector("[data-lb-description]");
    var linkEl = dialog.querySelector("[data-lb-link]");
    var labelEl = dialog.querySelector("[data-lb-label]");
    var closeBtn = dialog.querySelector("[data-lb-close]");
    var prevBtn = dialog.querySelector("[data-lb-prev]");
    var nextBtn = dialog.querySelector("[data-lb-next]");

    var currentIdx = 0;
    var lastTrigger = null;

    function open(idx) {
      var visible = getVisible();
      if (!visible.length) return;
      currentIdx = Math.max(0, Math.min(idx, visible.length - 1));
      populate(visible[currentIdx]);
      updateNav(visible.length);
      dialog.showModal();
      if (closeBtn) closeBtn.focus();
    }

    function populate(item) {
      if (imgEl) {
        // Reserve the correct box BEFORE the full image starts loading. Without
        // this the img falls back to its nominal 4:3 attributes and the modal
        // visibly jumps to the real height the moment the file decodes.
        var mediaEl = imgEl.parentElement;
        if (item.ratio) {
          imgEl.style.aspectRatio = String(item.ratio);
          if (mediaEl)
            mediaEl.style.setProperty("--lb-ratio", String(item.ratio));
        }

        // The grid thumbnail is already decoded, so it paints instantly and
        // stands in as a low-res preview while the 1200px version arrives.
        if (mediaEl && item.thumbUrl) {
          mediaEl.style.backgroundImage = 'url("' + item.thumbUrl + '")';
        }

        imgEl.classList.remove("is-loaded");
        imgEl.alt = item.altText || item.title;

        var reveal = function () {
          imgEl.classList.add("is-loaded");
          if (mediaEl) mediaEl.style.backgroundImage = "";
        };

        // NB: do NOT use imgEl.decode() here. populate() runs before
        // dialog.showModal(), so the image still sits inside a display:none
        // dialog and the decode promise never settles — the artwork loads but
        // stays at opacity 0 forever. onload/onerror always fire.
        // Assigned before src so a cache hit can't beat the handler.
        imgEl.onload = reveal;
        imgEl.onerror = reveal; // never leave the modal blank on a bad image
        imgEl.src = item.fullUrl;
        if (imgEl.complete && imgEl.naturalWidth) reveal();
      }
      if (titleEl) titleEl.textContent = item.title;
      if (descEl) descEl.textContent = item.description;
      if (linkEl) {
        var catUrl = CATEGORY_PAGES[item.category];
        var catLabel = CATEGORY_LABELS[item.category];
        if (catUrl && catLabel) {
          linkEl.href = catUrl;
          if (labelEl) labelEl.textContent = catLabel;
          linkEl.hidden = false;
        } else {
          linkEl.hidden = true;
        }
      }
    }

    function updateNav(total) {
      if (prevBtn) prevBtn.disabled = currentIdx === 0;
      if (nextBtn) nextBtn.disabled = currentIdx === total - 1;
    }

    function close() {
      dialog.close();
      if (lastTrigger) lastTrigger.focus();
    }

    // Open on image click
    masonry.addEventListener("click", function (e) {
      var trigger = e.target.closest("[data-lightbox-trigger]");
      if (!trigger) return;
      lastTrigger = trigger;

      var clickedGlobal = parseInt(trigger.dataset.index, 10);
      var visible = getVisible();
      var pos = -1;
      for (var i = 0; i < visible.length; i++) {
        if (visible[i].globalIndex === clickedGlobal) {
          pos = i;
          break;
        }
      }
      if (pos === -1) return;
      open(pos);
    });

    // Close button
    if (closeBtn) closeBtn.addEventListener("click", close);

    // Backdrop click (clicking the dialog element itself, outside .inner)
    dialog.addEventListener("click", function (e) {
      if (e.target === dialog) close();
    });

    // Prev / Next buttons
    if (prevBtn)
      prevBtn.addEventListener("click", function () {
        open(currentIdx - 1);
      });
    if (nextBtn)
      nextBtn.addEventListener("click", function () {
        open(currentIdx + 1);
      });

    // Arrow key navigation within dialog
    dialog.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        open(currentIdx - 1);
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        open(currentIdx + 1);
      }
    });

    // Intercept native Escape/cancel to control focus return
    dialog.addEventListener("cancel", function (e) {
      e.preventDefault();
      close();
    });

    // Clear the image after close so the next open never flashes the previous
    // piece. Removing the attribute rather than blanking it: src="" resolves
    // against the document URL, so the element ends up pointing at the page
    // itself and sits in a broken-image state, which WebKit will re-request and
    // which paints a broken-image glyph for a frame on reopen.
    dialog.addEventListener("close", function () {
      if (imgEl) {
        imgEl.removeAttribute("src");
        imgEl.alt = "";
      }
    });
  }

  // ─── Boot ──────────────────────────────────────────────────────────────────

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
